import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faRobot, faUser, faUserMd, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useGetAdminCommerceChatMessagesQuery } from '../../store/admin/adminApi';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';

interface AdminChatModalProps {
  comercioKey: string;
  conversationId: number;
  onClose: () => void;
}

const AdminChatModal: React.FC<AdminChatModalProps> = ({ comercioKey, conversationId, onClose }) => {
  const { i18n } = useTranslation();
  const dateLocale = i18n.language.startsWith('es') ? es : enUS;

  const { data: messages, isLoading, error } = useGetAdminCommerceChatMessagesQuery({ comercioKey, conversationId });
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-[#111111] border border-white/10 rounded-2xl w-full max-w-3xl h-[80vh] flex flex-col overflow-hidden shadow-2xl relative animate-in fade-in zoom-in duration-200">
        
        {/* HEADER */}
        <div className="p-4 border-b border-white/10 bg-white/5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-bold text-white">Thread #{conversationId}</h3>
            <span className="px-2 py-1 bg-pink-500/10 text-pink-400 text-xs font-bold rounded-md border border-pink-500/20">Read-Only</span>
          </div>
          <button 
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/50 hover:text-white transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* MESSAGES AREA */}
        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4 custom-scrollbar">
          {isLoading && (
            <div className="flex-1 flex items-center justify-center">
              <FontAwesomeIcon icon={faSpinner} className="text-3xl text-pink-500 animate-spin" />
            </div>
          )}
          
          {error && (
            <div className="p-4 text-center text-red-400 bg-red-400/10 rounded-xl">
              Failed to load messages.
            </div>
          )}

          {!isLoading && !error && messages?.map((msg) => {
            const isUser = msg.role === 'user';
            const isBot = msg.role === 'assistant' && msg.bot_id !== null;
            const isOperator = msg.role === 'assistant' && msg.bot_id === null;

            return (
              <div key={msg.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] flex flex-col gap-1 ${isUser ? 'items-end' : 'items-start'}`}>
                  {/* Message Bubble */}
                  <div className={`p-4 rounded-2xl shadow-lg relative ${
                    isUser 
                      ? 'bg-blue-600 text-white rounded-tr-sm' 
                      : isBot 
                        ? 'bg-[#1a1a1a] border border-white/10 text-white/90 rounded-tl-sm'
                        : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-tl-sm'
                  }`}>
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">{msg.content}</p>
                  </div>
                  
                  {/* Metadata underneath */}
                  <div className="flex items-center gap-2 text-[10px] text-white/40 px-1 mt-1">
                    <span>{format(new Date(msg.created_at), 'HH:mm', { locale: dateLocale })}</span>
                    
                    {isBot && (
                      <span className="flex items-center gap-1 text-white/60">
                        <FontAwesomeIcon icon={faRobot} /> Bot {msg.bot_id}
                        {msg.response_time_ms && ` (${msg.response_time_ms}ms)`}
                      </span>
                    )}
                    {isOperator && (
                      <span className="flex items-center gap-1 text-pink-400/80">
                        <FontAwesomeIcon icon={faUserMd} /> Operator
                      </span>
                    )}
                    {isUser && (
                      <span className="flex items-center gap-1 text-blue-400/80">
                        <FontAwesomeIcon icon={faUser} /> Patient
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {messages?.length === 0 && (
            <div className="flex-1 flex items-center justify-center text-white/40">
              No messages in this thread.
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* FOOTER (READ ONLY) */}
        <div className="p-4 border-t border-white/10 bg-white/5 text-center text-sm text-white/30">
          This is a read-only view for superadmins.
        </div>
      </div>
    </div>
  );
};

export default AdminChatModal;
